import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams } from '@/services/core/listSearch'
import { sortParams, type ListSort } from '@/services/core/paging'
import type { Targeting } from '@/services/targeting/targeting'
import { mapRateLimits, type RateLimit } from '@/services/earnings/rateLimits'
import { mapBudgetPeriods, type BudgetPeriod } from './budgets'
import type { Fee } from '@/services/earnings/fees'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import { formatCount } from '@/utils/format'
import { formatMoney } from '@/utils/money'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export type LineItemType = 'AUCTION' | 'SPONSORSHIP' | 'SECOND_PRICE_AUCTION' | 'CREATIVE_TAG'

/** How delivery is paced over the flight — mirrors the backend DeliveryCurve enum. */
export const DELIVERY_CURVES = [
  'EVEN',
  'FRONT_LOADED',
  'FAST_AS_POSSIBLE',
  'CAPPED',
  'GREEDY',
  'CATCH_UP',
  'DEFER_TO_ORDER',
] as const
export type DeliveryCurve = (typeof DELIVERY_CURVES)[number]

export interface CurrencyAmount {
  currency: string
  amount: number
}

/**
 * How a line item bids. Mirrors `BidSpecification.BidStrategy` in core, minus
 * FLOOR, which is not offered in the UI:
 *   - IMPRESSION ("standard") bids the CPM as set.
 *   - VIEWABLE ("vCPM") scales the bid by the request's expected viewability.
 *   - DYNAMIC bids up to the CPM, optimising towards a `optimiseFor` goal.
 */
export const BID_STRATEGIES = ['IMPRESSION', 'VIEWABLE', 'DYNAMIC'] as const
export type BidStrategy = (typeof BID_STRATEGIES)[number]

/** What a strategy bids on by default when the line item has not picked one. */
export const DEFAULT_BID_STRATEGY: BidStrategy = 'IMPRESSION'

/**
 * The event a DYNAMIC bid strategy optimises towards — an `EventType` in core.
 * VIEWABLE is the MRC-style view (50% for a second or more). Anything else,
 * such as attention, is a custom event the network settings allow.
 */
export const BIDDING_GOALS = ['IMPRESSION', 'CLICK', 'VIEWABLE'] as const
export type BiddingGoal = (typeof BIDDING_GOALS)[number]

/** The goal a dynamic strategy falls back to when none is set. */
export const DEFAULT_BIDDING_GOAL: BiddingGoal = 'IMPRESSION'

/** The API wraps the goal in an event target, which may also be a custom event. */
export interface BiddingEventTarget {
  eventType?: BiddingGoal | 'CUSTOM'
  customEventType?: { id: string; name?: string }
}

export interface BidSpecification {
  cpm?: CurrencyAmount
  cpc?: CurrencyAmount
  cpa?: CurrencyAmount
  bidStrategy?: BidStrategy
  /** Only meaningful for a DYNAMIC strategy; ignored for every other one. */
  optimiseFor?: BiddingEventTarget
  /** Fees set against this line item directly. Editable by the seller. */
  cpmFees?: Fee[]
  /** The platform's own fee, derived by the server from the network's terms. */
  cpmTechFee?: Fee
  /** Server-derived fees for the third-party audiences the line item targets. */
  audienceFees?: Fee[]
  /** Extra marketplace fees, added by ad ops rather than by the advertiser. */
  supplementaryFees?: Fee[]
}

/**
 * A goal as the form holds it: one of the standard events, or a custom event
 * the network allows dynamic bidding on, as `custom:<custom event id>`.
 */
export type BiddingGoalValue = BiddingGoal | `custom:${string}`

export function customBiddingGoal(customEventId: string): BiddingGoalValue {
  return `custom:${customEventId}`
}

/** The custom event a goal optimises towards, if it is a custom one. */
export function customEventIdOfGoal(goal: BiddingGoalValue): string | undefined {
  return goal.startsWith('custom:') ? goal.slice('custom:'.length) : undefined
}

/** The line item's goal — a standard event, or the custom event it optimises towards. */
export function biddingGoalOf(bid: BidSpecification | undefined): BiddingGoalValue | undefined {
  const target = bid?.optimiseFor
  if (target?.customEventType?.id) return customBiddingGoal(target.customEventType.id)
  const eventType = target?.eventType
  return BIDDING_GOALS.includes(eventType as BiddingGoal) ? (eventType as BiddingGoal) : undefined
}

/**
 * The event target a goal is saved as. The API replaces the whole target, so a
 * standard goal also drops any custom event the line item optimised for before.
 */
export function biddingEventTargetOf(goal: BiddingGoalValue): BiddingEventTarget {
  const customEventId = customEventIdOfGoal(goal)
  return customEventId
    ? { eventType: 'CUSTOM', customEventType: { id: customEventId } }
    : { eventType: goal as BiddingGoal }
}

export interface LineItemObjectives {
  IMPRESSION?: number
  CLICK?: number
  VIEWABLE_IMPRESSION?: number
  VISIBLE_IMPRESSION?: number
  RENDERED_IMPRESSION?: number
  CONVERSION?: number
  BUDGET?: CurrencyAmount
}

/**
 * How often the same creative may appear on one page — `deduplicationLevel` in
 * the API.
 *   - `NONE`: open, the same creative can be delivered several times on a page.
 *   - `CREATIVE`: unique, no creative of this line item repeats on a page.
 *   - `LINE_ITEM`: at most one creative of this line item per page.
 */
export type CreativeDelivery = 'NONE' | 'CREATIVE' | 'LINE_ITEM'

/**
 * How the line item spreads delivery over its creatives. The API models this
 * as `optimiseCreativesFor`: unset shows every creative equally, `CLICK` gives
 * the creatives with the better click rate more of the impressions.
 */
export type CreativeWeighting = 'EQUAL' | 'CLICK'

export interface LineItem {
  id: string
  name: string
  objectState: string
  /** Owning network — only populated by list queries that span networks. */
  /** System-set delivery state: RUNNING | READY | NOT_READY | ENDED. */
  executionState: string
  /** User-set state: PROPOSED | SUBMITTED | RESERVED | APPROVED | PAUSED | STOPPED | COMPLETED. */
  userState: string
  orderId: string
  orderName: string
  advertiserId: string
  advertiserName: string
  type: LineItemType
  /** Set when the line item is backed by a marketplace (programmatic) product. */
  axProductId?: string
  startDate: string
  endDate: string
  bidSpecification: BidSpecification
  objectives: LineItemObjectives
  /** Currency a creative tag line item is billed and bid in. */
  billingCurrency?: string
  /**
   * Budget split over periods. An alternative to `objectives.BUDGET` — a line
   * item has one or the other, never both — and when it is set the line item's
   * own dates are the first period's start and the last period's end.
   */
  budget: BudgetPeriod[]
  /** Whether what one period leaves unspent is added to the next one. */
  budgetRollover: boolean
  /** How much of each objective has been delivered so far. Read-only. */
  objectiveDelivery?: LineItemObjectives
  /** Lifetime impressions delivered. Read-only. */
  impressionDelivery?: number
  /** Lifetime rendered impressions delivered. Read-only. */
  renderedDelivery?: number
  sponsorshipPercentage?: number
  smoothed: boolean
  deliveryCurve?: DeliveryCurve
  /** Page-level deduplication of this line item's creatives. */
  creativeDelivery: CreativeDelivery
  /** Equal rotation, or click-optimised rotation, of the creatives. */
  creativeWeighting: CreativeWeighting
  externalReference: string
  /** Programmatic deal this line item trades on. Empty when it has none. */
  dealId: string
  /**
   * The election, or other political activity, this line item advertises for.
   * Empty on a line item that is not a political ad.
   */
  electionId: string
  electionName: string
  /**
   * The topic within the election the line item's message is about — a topic
   * name (or id) taken from the political campaign the advertiser runs for
   * that election. Empty when none is set.
   */
  politicalCampaignTopic: string
  /** What the line item targets. Every target is empty in concise list responses. */
  targeting: Targeting
  /**
   * Whether `targeting` is the line item's own, so a list can show it without
   * fetching the line item — false for concise lists, which only count targets.
   */
  targetingComplete: boolean
  /** Delivery caps — per-user frequency capping and line item-wide limits. */
  rateLimits: RateLimit[]
  /** Ids of the notes on the line item — the bodies come from `NotesApi`. */
  noteIds: string[]
  /** Server-side problems with the line item, some of which stop it serving. */
  validationWarnings: ValidationWarning[]
  updateTime: string
  createTime: string
  deliveryPercentage?: number
  deliveryPercentageYesterday?: number
  deliveryPercentageToday?: number
  elapsedTimePercentage?: number
  elapsedTimePercentageToday?: number
}

export interface LineItemInput {
  name: string
  objectState?: string
  /** User-set state — see `UserState` in UserStateSelect for the vocabulary. */
  userState?: string
  order?: { id: string }
  /** Marketplace product id, or null to unbook the line item from one. */
  axProduct?: string | null
  type?: LineItemType
  startDate?: string | null
  endDate?: string | null
  bidSpecification?: BidSpecification
  objectives?: LineItemObjectives
  /** Creative tag line items only; left out, the stored currency is kept. */
  billingCurrency?: string
  /** Posted whole — a period left out of the payload is removed. */
  budget?: BudgetPeriod[]
  budgetRollover?: boolean
  sponsorshipPercentage?: number
  /** Sending this lets the backend derive `smoothed` on its own. */
  deliveryCurve?: DeliveryCurve
  deduplicationLevel?: CreativeDelivery
  /** `null` clears the optimisation, which is what equal weighting means. */
  optimiseCreativesFor?: 'CLICK' | null
  externalReference?: string
  /** `null` removes the deal from the line item. */
  dealId?: string | null
  /**
   * Election id, as the API takes it — a bare id string, or `null` to detach
   * the line item from the election it had.
   */
  election?: string | null
  /** Topic within that election; `null` clears it. */
  politicalCampaignTopic?: string | null
  /** Posted whole: every target the line item should keep must be in here. */
  targeting?: Targeting
  /** Also posted whole — a cap left out of the payload is removed. */
  rateLimits?: RateLimit[]
}

/**
 * The curve a line item paces its delivery on. The API derives `smoothed` from
 * the curve, and a line item that predates the curve carries only that flag —
 * so an absent curve is read back off it.
 */
export function deliveryCurveOf(
  lineItem: { deliveryCurve?: DeliveryCurve; smoothed?: boolean } | null | undefined
): DeliveryCurve {
  if (lineItem?.deliveryCurve) return lineItem.deliveryCurve
  return lineItem?.smoothed === false ? 'FAST_AS_POSSIBLE' : 'EVEN'
}

/**
 * The objective a line item is judged on. The API can hold several at once,
 * but delivery is reported against one, and this is the order the ad server
 * and the UI both read them in.
 */
export const OBJECTIVE_PRIORITY: (keyof LineItemObjectives)[] = [
  'BUDGET',
  'IMPRESSION',
  'CLICK',
  'VIEWABLE_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'RENDERED_IMPRESSION',
  'CONVERSION',
]

/** The objective to show, with its target — or null when none is set. */
export function primaryObjective(
  objectives: LineItemObjectives | undefined
): { type: keyof LineItemObjectives; target: number | CurrencyAmount } | null {
  if (!objectives) return null
  for (const type of OBJECTIVE_PRIORITY) {
    const target = objectives[type]
    if (target != null) return { type, target }
  }
  return null
}

function isMoney(value: number | CurrencyAmount): value is CurrencyAmount {
  return typeof value === 'object' && value !== null && 'amount' in value
}

/** An objective's target or delivery as written on screen — money or a count. */
export function formatObjectiveValue(value: number | CurrencyAmount): string {
  return isMoney(value)
    ? formatMoney(value.amount, value.currency, 0)
    : formatCount(value)
}

/**
 * Reads a reference the API may send either way: as a `{id, name}` object, or
 * — as the election is declared — as a bare id string.
 */
function refOf(value: unknown): { id: string; name: string } {
  if (typeof value === 'string') return { id: value, name: '' }
  const r = value as Record<string, unknown> | undefined
  return { id: (r?.id as string) ?? '', name: (r?.name as string) ?? '' }
}

/**
 * Decimal places the ad server keeps money in, per currency. Seven unless
 * listed (`CurrencyCodeUtil` in core).
 */
const MONEY_SCALE: Record<string, number> = { JPY: 5, HUF: 5, THB: 6, AED: 6 }

/**
 * Delivered budget comes back as money only when the line item has a budget
 * objective. With budget periods instead, the API has no currency to hand and
 * sends the bare fixed-point value, so it is turned into money here in the
 * periods' currency.
 */
function mapObjectiveDelivery(
  value: unknown,
  budget: BudgetPeriod[]
): LineItemObjectives | undefined {
  const delivery = value as (Omit<LineItemObjectives, 'BUDGET'> & { BUDGET?: unknown }) | undefined
  if (typeof delivery?.BUDGET !== 'number') return delivery as LineItemObjectives | undefined
  const currency = budget.find((p) => p.amount?.currency)?.amount?.currency
  if (!currency) {
    const { BUDGET: _raw, ...rest } = delivery
    return rest
  }
  const amount = delivery.BUDGET / 10 ** (MONEY_SCALE[currency] ?? 7)
  return { ...delivery, BUDGET: { amount, currency } }
}

function mapLineItem(r: Record<string, unknown>, concise = false): LineItem {
  const budget = mapBudgetPeriods(r.budget)
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    executionState: (r.executionState as string) ?? '',
    userState: (r.userState as string) ?? '',
    orderId: ((r.order as Record<string, unknown>)?.id as string) ?? '',
    orderName: ((r.order as Record<string, unknown>)?.name as string) ?? '',
    advertiserId: refOf(r.advertiser).id || refOf(r.readOnlyAdvertiser).id,
    advertiserName: refOf(r.advertiser).name || refOf(r.readOnlyAdvertiser).name,
    type: (r.type as LineItemType) ?? 'AUCTION',
    axProductId:
      ((r.axProduct as Record<string, unknown>)?.id as string) ??
      (typeof r.axProduct === 'string' ? r.axProduct : undefined),
    startDate: (r.startDate as string) ?? '',
    endDate: (r.endDate as string) ?? '',
    bidSpecification: (r.bidSpecification as BidSpecification) ?? {},
    objectives: (r.objectives as LineItemObjectives) ?? {},
    billingCurrency: r.billingCurrency as string | undefined,
    budget,
    // The API leaves the flag out until a budget list is set; rollover is the default.
    budgetRollover: (r.budgetRollover as boolean) ?? true,
    objectiveDelivery: mapObjectiveDelivery(r.objectiveDelivery, budget),
    impressionDelivery: r.impressionDelivery as number | undefined,
    renderedDelivery: r.renderedDelivery as number | undefined,
    sponsorshipPercentage: r.sponsorshipPercentage as number | undefined,
    smoothed: (r.smoothed as boolean) ?? false,
    deliveryCurve: r.deliveryCurve as DeliveryCurve | undefined,
    creativeDelivery: (r.deduplicationLevel as CreativeDelivery) ?? 'NONE',
    creativeWeighting: r.optimiseCreativesFor === 'CLICK' ? 'CLICK' : 'EQUAL',
    externalReference: (r.externalReference as string) ?? '',
    dealId: (r.dealId as string) ?? '',
    electionId: refOf(r.election).id,
    electionName: refOf(r.election).name,
    politicalCampaignTopic: (r.politicalCampaignTopic as string) ?? '',
    targeting: (r.targeting as Targeting) ?? {},
    targetingComplete: !concise,
    rateLimits: mapRateLimits(r.rateLimits),
    noteIds: ((r.notes as Record<string, unknown>[]) ?? [])
      .map((n) => (n.id as string) ?? '')
      .filter(Boolean),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    deliveryPercentage: r.deliveryPercentage as number | undefined,
    deliveryPercentageYesterday: r.deliveryPercentageYesterday as number | undefined,
    deliveryPercentageToday: r.deliveryPercentageToday as number | undefined,
    elapsedTimePercentage: r.elapsedTimePercentage as number | undefined,
    elapsedTimePercentageToday: r.elapsedTimePercentageToday as number | undefined,
  }
}

// ─── Filter params ─────────────────────────────────────────────────────────

/** Delivery statuses that can be filtered on — see DeliveryStatusBadge for the vocabulary. */
export type DeliveryStatusFilter =
  | 'all'
  | 'RUNNING'
  | 'READY'
  | 'NOT_READY'
  | 'ENDED'
  | 'PAUSED'
  | 'STOPPED'
  | 'PROPOSED'

export interface LineItemFetchFilters {
  objectState?: 'all' | 'ACTIVE' | 'INACTIVE'
  deliveryState?: DeliveryStatusFilter
  /** Free text matched against the line item's name and references. */
  search?: string
  /** Only line items belonging to this advertiser. */
  advertiserId?: string
  /**
   * Only line items whose order belongs to a team in this country (ISO code).
   * The API ignores it when an advertiser is also given.
   */
  teamCountry?: string
}

/**
 * Maps UI filter state to API query parameters.
 * Uses `includeActive`/`includeInactive` for object state, the `where`
 * mini-query language for delivery/execution state, and the endpoint's own
 * `advertiser` parameter for the advertiser.
 *
 * See docs/api-query-params.md for the full where-clause reference.
 */
function buildFilterParams(filters: LineItemFetchFilters): Record<string, string> {
  const p: Record<string, string> = {}

  // Object state → include flags
  const os = filters.objectState ?? 'ACTIVE'
  p.includeActive = os === 'all' || os === 'ACTIVE' ? 'true' : 'false'
  p.includeInactive = os === 'all' || os === 'INACTIVE' ? 'true' : 'false'
  p.includeHidden = 'false'

  // Delivery status → where clause (executionState is system-set, userState user-set)
  const whereParts: string[] = []
  switch (filters.deliveryState) {
    case 'RUNNING':
      whereParts.push('lineItem.executionState in RUNNING')
      break
    case 'READY':
      whereParts.push('lineItem.executionState in READY')
      break
    case 'NOT_READY':
      whereParts.push('lineItem.executionState in NOT_READY')
      break
    case 'ENDED':
      whereParts.push('lineItem.executionState in ENDED')
      break
    case 'PAUSED':
      whereParts.push('lineItem.userState in PAUSED')
      break
    case 'STOPPED':
      whereParts.push('lineItem.userState in STOPPED')
      break
    case 'PROPOSED':
      whereParts.push('lineItem.userState in PROPOSED')
      break
  }
  if (whereParts.length) p.where = whereParts.join(';')

  if (filters.advertiserId) p.advertiser = filters.advertiserId
  if (filters.teamCountry) p.teamCountry = filters.teamCountry

  Object.assign(p, searchParams('lineItems', filters.search))

  return p
}

/** One line item of a bulk save: the id, and only the fields being changed. */
export type LineItemBulkInput = Partial<LineItemInput> & { id: string }

export interface LineItemPage {
  results: LineItem[]
  totalCount: number
  page: number
  pageSize: number
}

/**
 * A free-form list query: raw `where` clauses (see docs/api-query-params.md)
 * and any sort order. Only active, unhidden line items are listed.
 */
export interface LineItemQuery {
  where?: string[]
  orderBy?: string
  orderByDirection?: 'ASCENDING' | 'DESCENDING'
  page?: number
  pageSize?: number
}

export const LineItemsApi = {
  query: async ({
    where = [],
    orderBy = 'name',
    orderByDirection = 'ASCENDING',
    page = 1,
    pageSize = 100,
  }: LineItemQuery): Promise<LineItemPage> => {
    const params = new URLSearchParams({
      context: getContext(),
      concise: 'true',
      includeActive: 'true',
      includeInactive: 'false',
      includeHidden: 'false',
      page: String(page),
      pageSize: String(pageSize),
      orderBy,
      orderByDirection,
      nullsOrderPolicy: 'LAST',
    })
    if (where.length) params.set('where', where.join(';'))
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    const data = await res.json()
    const results = toArray(data).map((r) => mapLineItem(r, true))
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /**
   * Pages through a query until every match is in, or `maxPages` pages have
   * been read. `totalCount` tells the caller whether the cap cut it short.
   */
  queryAll: async (
    query: Omit<LineItemQuery, 'page' | 'pageSize'>,
    maxPages = 5
  ): Promise<{ results: LineItem[]; totalCount: number }> => {
    const results: LineItem[] = []
    let totalCount = 0
    for (let page = 1; page <= maxPages; page++) {
      const next = await LineItemsApi.query({ ...query, page, pageSize: 100 })
      results.push(...next.results)
      totalCount = next.totalCount
      if (results.length >= totalCount || next.results.length < 100) break
    }
    return { results, totalCount }
  },

  getPage: async (
    page = 1,
    pageSize = 25,
    filters: LineItemFetchFilters = {},
    sort?: ListSort | null
  ): Promise<LineItemPage> => {
    const params = new URLSearchParams({
      context: getContext(),
      page: String(page),
      pageSize: String(pageSize),
      ...sortParams(sort, {
        orderBy: 'name',
        orderByDirection: 'ASCENDING',
        nullsOrderPolicy: 'FIRST',
      }),
      ...buildFilterParams(filters),
    })
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    const data = await res.json()
    const results = toArray(data).map((r) => mapLineItem(r))
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  getAll: async (filters: LineItemFetchFilters = {}): Promise<LineItem[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      concise: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
      ...buildFilterParams(filters),
    })
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    const data = await res.json()
    return toArray(data).map((r) => mapLineItem(r, true))
  },

  getByAdvertiser: async (
    advertiserId: string,
    filters: LineItemFetchFilters = {}
  ): Promise<LineItem[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      advertiser: advertiserId,
      pageSize: '100',
      page: '1',
      orderBy: 'name',
      ...buildFilterParams(filters),
    })
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    return toArray(await res.json()).map((r) => mapLineItem(r))
  },

  getByOrder: async (orderId: string, filters: LineItemFetchFilters = {}): Promise<LineItem[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      order: orderId,
      pageSize: '100',
      page: '1',
      orderBy: 'name',
      ...buildFilterParams(filters),
    })
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    return toArray(await res.json()).map((r) => mapLineItem(r))
  },

  /**
   * Fetches several line items in one request. The API's `id` parameter is
   * semicolon-separated and cannot be combined with the order, advertiser or
   * team filters, so this is a plain lookup by id. Concise unless
   * `withTargeting`: targeting is all a concise line item leaves out.
   */
  getByIds: async (
    ids: string[],
    { withTargeting = false }: { withTargeting?: boolean } = {}
  ): Promise<LineItem[]> => {
    if (ids.length === 0) return []
    const params = new URLSearchParams({
      context: getContext(),
      // Concise leaves the targeting out, which a list showing it needs.
      ...(withTargeting ? {} : { concise: 'true' }),
      id: ids.join(';'),
      pageSize: String(ids.length),
    })
    const res = await apiFetch(`${API_BASE}/lineitems?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch line items (${res.status})`)
    return toArray(await res.json()).map((r) => mapLineItem(r, !withTargeting))
  },

  getById: async (id: string): Promise<LineItem> => {
    // Without showBudgetSpend the budget periods come back without their spend
    // and rolled-over (adjusted) amounts.
    const res = await apiFetch(`${API_BASE}/lineitems/${id}?context=${getContext()}&showBudgetSpend=true`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch line item (${res.status})`)
    return mapLineItem(await res.json())
  },

  /**
   * The line item exactly as the API returns it, unmapped — what a comparison
   * or an export should show, rather than the app's own shape.
   */
  getRawById: async (id: string): Promise<unknown> => {
    const res = await apiFetch(
      `${API_BASE}/lineitems/${id}?context=${getContext()}&showBudgetSpend=true`,
      { headers: JSON_HEADERS }
    )
    if (!res.ok) throw new Error(`Failed to fetch line item (${res.status})`)
    return res.json()
  },

  /**
   * Copies a line item, optionally into another order. The copy's id is
   * generated by the caller, as everywhere else in the app, so it can navigate
   * to the copy without waiting on the response.
   */
  copy: async (
    id: string,
    input: {
      newId: string
      name: string
      orderId?: string
      userState?: string
      copyTargeting?: boolean
      copyCreatives?: boolean
      copyReportSchedules?: boolean
    }
  ): Promise<LineItem> => {
    const params = new URLSearchParams({
      context: getContext(),
      lineItemId: input.newId,
      name: input.name,
      copyTargeting: String(input.copyTargeting ?? true),
      copyCreatives: String(input.copyCreatives ?? true),
      copyReportSchedules: String(input.copyReportSchedules ?? true),
      ...(input.orderId ? { orderId: input.orderId } : {}),
      ...(input.userState ? { userState: input.userState } : {}),
    })
    const res = await apiFetch(`${API_BASE}/lineitems/${id}/copy?${params}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: '{}',
    })
    if (!res.ok) await throwApiError(res, 'Failed to copy line item')
    return mapLineItem(await res.json())
  },

  /**
   * Saves the line item. `ignoreProduct` tells the server to skip the booked
   * marketplace product's enforcement pass, which otherwise re-imposes the
   * product's own targeting and discards every targeting type the product does
   * not list as optional.
   */
  update: async (
    id: string,
    input: LineItemInput,
    options: { ignoreProduct?: boolean } = {}
  ): Promise<LineItem> => {
    const params = new URLSearchParams({ context: getContext() })
    if (options.ignoreProduct) params.set('ignoreProduct', 'true')
    const res = await apiFetch(`${API_BASE}/lineitems/${id}?${params}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save line item')
    return mapLineItem(await res.json())
  },

  /**
   * Saves several line items in one request. Each payload carries the id of
   * the line item it changes and only the fields it changes — the server
   * leaves every field the payload omits alone. The whole list is applied in
   * one transaction, so a rejected line item saves none of them.
   */
  updateMany: async (inputs: LineItemBulkInput[]): Promise<LineItem[]> => {
    const res = await apiFetch(`${API_BASE}/lineitems?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(inputs),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save line items')
    return toArray(await res.json()).map((r) => mapLineItem(r))
  },
}
